import { useCallback, useEffect, useMemo, useRef } from 'react';

import { GridApi, GridGroupNode, GridRowId, GridValidRowModel } from '@mui/x-data-grid-premium';

export interface UseTreeExpansionParams<R extends GridValidRowModel> {
  treeDataEnabled: boolean;
  gridApi: GridApi | null;
  dataGridKey: string | undefined;
  rows: R[];
  resolveRowId: (row: R) => string | number;
  forceExpandedTreeRowIds?: GridRowId[];
  defaultGroupingExpansionDepth?: number;
  getRowExpandedByDefault?: (node: GridGroupNode) => boolean | undefined;
  persistedCollapsedRowIds?: GridRowId[];
  onCollapsedRowIdsChange?: (ids: GridRowId[]) => void;
}

export interface UseTreeExpansionReturn {
  isGroupExpandedByDefault: (node: GridGroupNode) => boolean;
}

// MUI rebuilds the row tree from scratch whenever the rows prop is replaced and
// re-derives every group's expansion from `isGroupExpandedByDefault`, so a data
// refresh would reset what the user expanded or collapsed. Both directions are
// recorded here and answered from the callback, which keeps user choices stable
// across refreshes (AAD-3221). Group-by groups are synthetic nodes whose ids
// are not row ids, so entries must never be pruned against the row set; stale
// ids are harmless because no node carries them anymore.
// `persistedCollapsedRowIds`/`onCollapsedRowIdsChange` extend this across
// unmounts (e.g. page navigation): the collapsed set — not the expanded one,
// since the default is expand-all — is seeded on mount and reported back out
// on every change, so a caller can round-trip it through its own storage.
const useTreeExpansion = <R extends GridValidRowModel>({
  treeDataEnabled,
  gridApi,
  dataGridKey,
  rows,
  resolveRowId,
  forceExpandedTreeRowIds,
  defaultGroupingExpansionDepth,
  getRowExpandedByDefault,
  persistedCollapsedRowIds,
  onCollapsedRowIdsChange,
}: UseTreeExpansionParams<R>): UseTreeExpansionReturn => {
  const expandedRowIdsRef = useRef<Set<GridRowId>>(new Set());
  const collapsedRowIdsRef = useRef<Set<GridRowId>>(new Set(persistedCollapsedRowIds ?? []));

  // Kept fresh without re-subscribing the effects below on every render —
  // callers pass this inline (e.g. a store setter wrapped in a closure).
  const onCollapsedRowIdsChangeRef = useRef(onCollapsedRowIdsChange);
  onCollapsedRowIdsChangeRef.current = onCollapsedRowIdsChange;

  const persistCollapsedRowIds = useCallback(() => {
    onCollapsedRowIdsChangeRef.current?.(Array.from(collapsedRowIdsRef.current));
  }, []);

  const forceExpandedSet = useMemo(
    () => new Set(forceExpandedTreeRowIds ?? []),
    [forceExpandedTreeRowIds],
  );

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) => {
      // When a parent's children precede it in the rows array (pages id-sort their
      // rows), the rebuild consults this with an auto-generated placeholder whose
      // groupingKey is the real row id the refs are keyed by — the node is renamed
      // to that id afterwards without recomputing expansion, so the placeholder
      // consult is the only chance to answer correctly.
      const keys: Array<GridRowId> = [node.id];
      if (node.isAutoGenerated && node.groupingKey != null) {
        keys.push(node.groupingKey, String(node.groupingKey));
      }
      // A collapse recorded after a force-expand wins until the force set changes again.
      if (keys.some((key) => collapsedRowIdsRef.current.has(key))) return false;
      if (keys.some((key) => expandedRowIdsRef.current.has(key))) return true;
      if (keys.some((key) => forceExpandedSet.has(key))) return true;
      // Pages exempt specific parents from the expand-everything default (e.g.
      // bookings keep their redecoration sub-rows tucked away); user toggles and
      // force-expands above still override the exemption.
      const pageDefault = getRowExpandedByDefault?.(node);
      if (pageDefault !== undefined) return pageDefault;
      // MUI gives this callback priority over `defaultGroupingExpansionDepth`, so it must
      // reproduce the same `?? -1` default the grid prop uses — groups and packages are
      // expanded by default; only a user's collapse (or a page exemption) closes them.
      const expansionDepth = defaultGroupingExpansionDepth ?? -1;
      return expansionDepth === -1 || expansionDepth > (node.depth ?? 0);
    },
    [defaultGroupingExpansionDepth, forceExpandedSet, getRowExpandedByDefault],
  );

  // Subscribed per grid instance: a remount (grouping change, headers placeholder, list view)
  // hands out a new api whose events the previous subscription never sees.
  useEffect(() => {
    if (!treeDataEnabled || !gridApi) return;

    return gridApi.subscribeEvent('rowExpansionChange', (node: GridGroupNode) => {
      if (node.childrenExpanded) {
        expandedRowIdsRef.current.add(node.id);
        collapsedRowIdsRef.current.delete(node.id);
      } else {
        collapsedRowIdsRef.current.add(node.id);
        expandedRowIdsRef.current.delete(node.id);
      }
      persistCollapsedRowIds();
    });
  }, [treeDataEnabled, gridApi, persistCollapsedRowIds]);

  // Belt to the callback's braces: re-impose recorded choices on any node a
  // rebuild got wrong anyway (a rebuild path that skips the callback, or an id
  // form the key derivation above does not cover). A plain effect runs after the
  // grid's own rows effect in the same commit, so the tree is already rebuilt.
  useEffect(() => {
    if (!treeDataEnabled || !gridApi?.setRowChildrenExpansion || !gridApi.getRowNode) return;

    const repair = (ids: Set<GridRowId>, expanded: boolean) => {
      ids.forEach((id) => {
        const node = gridApi.getRowNode(id);
        if (node?.type === 'group' && node.childrenExpanded !== expanded) {
          try {
            // @ts-ignore — setRowChildrenExpansion
            gridApi.setRowChildrenExpansion(id, expanded);
          } catch (_) {
            // ignore — node may have been removed between render and effect
          }
        }
      });
    };
    repair(expandedRowIdsRef.current, true);
    repair(collapsedRowIdsRef.current, false);
  }, [treeDataEnabled, gridApi, rows]);

  // Changing the grouping remounts the grid (dataGridKey) and builds different
  // synthetic group nodes, so only entries backed by real rows carry over.
  const previousDataGridKeyRef = useRef(dataGridKey);
  useEffect(() => {
    if (previousDataGridKeyRef.current === dataGridKey) return;
    previousDataGridKeyRef.current = dataGridKey;

    const rowIds = new Set(rows.map((row) => String(resolveRowId(row))));
    const keepRealRowIds = (ids: Set<GridRowId>) => {
      ids.forEach((id) => {
        if (!rowIds.has(String(id))) ids.delete(id);
      });
    };
    keepRealRowIds(expandedRowIdsRef.current);
    keepRealRowIds(collapsedRowIdsRef.current);
    persistCollapsedRowIds();
  }, [dataGridKey, rows, resolveRowId, persistCollapsedRowIds]);

  useEffect(() => {
    if (!treeDataEnabled || forceExpandedSet.size === 0 || !gridApi?.setRowChildrenExpansion) {
      return;
    }

    forceExpandedSet.forEach((id) => {
      expandedRowIdsRef.current.add(id);
      collapsedRowIdsRef.current.delete(id);
      const node = gridApi.getRowNode(id);
      if (node?.type === 'group' && !node.childrenExpanded) {
        try {
          // @ts-ignore — setRowChildrenExpansion
          gridApi.setRowChildrenExpansion(id, true);
        } catch (_) {
          // ignore — node may have been removed between render and effect
        }
      }
    });
    persistCollapsedRowIds();
  }, [treeDataEnabled, gridApi, forceExpandedSet, persistCollapsedRowIds]);

  return { isGroupExpandedByDefault };
};

export default useTreeExpansion;
